import React, { useState, useEffect } from "react"
import _isEmpty from "lodash/isEmpty"

import {
  validateField,
  validatePhone,
  sanitizeObject,
  getPageInfo,
  getShortenedUrl,
} from "@/utils/helper"
import { apim } from "@/constants/api"
// import { log } from "@Services/logger.service";

import { validateRecaptcha } from "@/components/core/ReCaptcha/ReCaptcha"
import ContactInfo from "../Form/ContactInfo"
import BusinessInfo from "../Form/BusinessInfo"
import UploadDocument from "../Form/UploadDocument"
import SubmitFooter from "../Form/SubmitFooter"
import TradeMembershipConfirmation from "../TradeMembershipConfirmation"
import { addDataLayer } from "../analytics"

const TradeMembership = props => {
  const {
    i18n,
    submitSuccess,
    brandName,
    termsAndConsLink,
    isNewTab,
    subjectAccessRequest,
  } = props
  const [userData, setUserData] = useState({
    fname: "",
    lname: "",
    email: "",
    tradeCustomer: true,
    newsletterCheck: false,
    address: "",
    aptFloor: "",
    city: "",
    state: {},
    subJobType: "",
    zipcode: "",
    country: {},
    phone: "",
    company: "",
    isPrimaryAddress: false,
    companyWebsite: "",
    jobType: "",
    label: "",
    taxReq: false,
    termsAndConsCheck: false,
  })
  const [imageFiles, setImageFiles] = useState([])
  const [error, setError] = useState({})
  const [loading, setLoading] = useState(false)
  const [authoredConfirmSection, setAuthoredConfirmSection] = useState(null)
  const [authoredWelcomeSection, setAuthoredWelcomeSection] = useState(null)
  const [formTitle, setFormTitle] = useState(null)
  const [continueShoppingCTALink, setContinueShoppingCTALink] = useState("")
  const [recaptchaAnalytics, setRecaptchaAnalytics] = useState({})

  useEffect(() => {
    const confirmSection =
      document?.querySelectorAll?.("#confirmation-section")?.[0]
        ?.parentElement || null
    const welcomeSection =
      document
        ?.getElementById?.("form-section")
        ?.querySelectorAll?.("div.teaser")?.[0] || null
    const formTitleText =
      document
        ?.getElementById?.("trade-program-form-title")
        ?.querySelectorAll("h1")?.[0]
        ?.innerText?.toLowerCase() || null
    getRecaptchaAnalytics({ confirmSection, welcomeSection, formTitleText })
    getContinueShoppingUrl()
  }, [brandName])

  const getRecaptchaAnalytics = obj => {
    const { confirmSection, welcomeSection, formTitleText } = obj
    const page = getPageInfo()
    const pageName = page?.category?.subCategory1?.toLowerCase()
    const pageType = page?.category?.pageType?.toLowerCase()
    setRecaptchaAnalytics({
      addNoFooterInAttr: true,
      pageTitle: `${pageName}:${formTitleText}`,
      internalLinkPosition: `${pageName}`,
      internalLinkType: `${pageType}:navigation`,
    })
    setFormTitle(formTitleText)
    setAuthoredConfirmSection(confirmSection)
    setAuthoredWelcomeSection(welcomeSection)
  }

  const getContinueShoppingUrl = async () => {
    const url = (await getShortenedUrl(props?.continueShoppingUrl)) || "/"
    setContinueShoppingCTALink(url)
  }

  const validateExist = field => {
    switch (field) {
      case "fname":
      case "lname":
        if (
          !userData[field].trim() ||
          !validateField("name", userData[field])
        ) {
          return field === "fname" ? i18n.fnameError : i18n.lnameError
        }
        break
      case "email":
        if (!userData[field]) return i18n.noEmailError
        else if (!validateField("email", userData[field]))
          return i18n.validEmail
        break
      case "phone":
        if (!userData.phone) return i18n.noPhoneError
        else if (!validatePhone(userData.phone)) return i18n.validPhone
        break
      case "zipcode":
        if (!userData.zipcode.trim()) return i18n.zipcodeError
        else if (userData.zipcode.length < 5) return i18n.validZipcode
        break
      case "company":
        if (
          userData.company.trim() === "" ||
          !validateField("company", userData[field])
        )
          return i18n.noCompanyError
        break
      case "address":
        if (!userData.address.trim()) return i18n.addressError
        break
      case "city":
        if (!userData.city.trim()) return i18n.cityError
        break
      case "state":
        if (!userData.state || _isEmpty(userData.state)) return i18n.stateError
        break
      case "companyWebsite":
        if (
          userData.companyWebsite.trim() === "" ||
          !validateField("website", userData[field])
        )
          return i18n.companyWebsiteError
        break
      case "jobType":
        if (userData.jobType.trim() === "") return i18n.jobTypeError
        break
      case "subJobType":
        if (
          document.getElementById("contact-user-subJobType") &&
          userData.subJobType.trim() === ""
        )
          return i18n.subJobTypeError
        break
      default:
        return
    }
  }

  const onBlurError = field => {
    setError({
      ...error,
      [field]: validateExist(field),
    })
  }

  const getInternalLinkURLS = url => {
    return url?.startsWith("/") || url?.includes(window.location.hostname)
      ? "true"
      : "false"
  }

  const getErrorList = () => {
    let errorList = {
      fname: validateExist("fname"),
      lname: validateExist("lname"),
      email: validateExist("email"),
      zipcode: validateExist("zipcode"),
      phone: validateExist("phone"),
      company: validateExist("company"),
      address: validateExist("address"),
      city: validateExist("city"),
      state: validateExist("state"),
      companyWebsite: validateExist("companyWebsite"),
      jobType: validateExist("jobType"),
      subJobType: validateExist("subJobType"),
    }
    return errorList
  }

  const onSubmit = () => {
    setLoading(true)
    let errorDataList = []
    const errorList = getErrorList()
    const imageLinks = imageFiles.map(image => ({
      link: image.link,
      success: true,
    }))

    setError(errorList)
    if (errorList.fname) {
      errorDataList.push(`contact-user-fname`)
    } else if (errorList.lname) {
      errorDataList.push(`contact-user-lname`)
    } else if (errorList.email) {
      errorDataList.push(`contact-user-email`)
    } else if (errorList.zipcode) {
      errorDataList.push(`contact-user-zipcode`)
    } else if (errorList.phone) {
      errorDataList.push(`contact-user-phone`)
    } else if (errorList.company) {
      errorDataList.push(`kf-company-name`)
    } else if (errorList.address) {
      errorDataList.push(`contact-user-address`)
    } else if (errorList.city) {
      errorDataList.push(`contact-user-city`)
    } else if (errorList.state) {
      errorDataList.push(`contact-user-state`)
    } else if (errorList.companyWebsite) {
      errorDataList.push(`kf-company-website`)
    } else if (errorList.jobType) {
      errorDataList.push(`contact-user-job-type`)
    } else if (errorList.subJobType) {
      errorDataList.push(`contact-user-subJobType`)
    }

    errorDataList.forEach((item, i) => {
      if (i === 0) document.getElementById(`${item}`).focus()
    })

    const isInvalid = Object.values(errorList).find(el => el)
    const {
      fname,
      lname,
      email,
      tradeCustomer,
      newsletterCheck,
      address,
      aptFloor,
      city,
      state,
      subJobType,
      zipcode,
      country,
      phone,
      company,
      isPrimaryAddress,
      companyWebsite,
      jobType,
      label,
      taxReq,
      termsAndConsCheck,
    } = userData

    if (!isInvalid) {
      let body = {
        FirstName: fname,
        LastName: lname,
        Email: email,
        Address1: address,
        Address2: aptFloor,
        City: city,
        StateProvince: state.code,
        PostalCode: zipcode,
        Country: country.code,
        Phone: phone,
        IsPrimaryAddress: isPrimaryAddress,
        AddressLabel: label,
        AttachmentLinks: imageLinks,
        Company: company,
        CompanyWebsite: companyWebsite,
        JobType: jobType,
        JobSubType: subJobType,
        IsUserExistingTradeCustomer: tradeCustomer,
        RequestTaxExemptionStatus: taxReq,
        "Subscribe Ann Sacks": newsletterCheck,
      }

      sanitizeObject(body)
      setLoading(false)
      validateRecaptcha().then(res => {
        if (res.success) {
          apim
            .post(`/membership/trade?brand=${brandName}`, body)
            .then(() => {
              props.handleSubmitMsg(true)
              authoredWelcomeSection?.classList?.add(
                "trade-membership__success--hidden"
              )
              authoredConfirmSection?.classList?.remove(
                "container-content-hide"
              )
              addDataLayer({
                title: "submit",
                sts: "success",
                formTitle,
                jobType: jobType?.toLowerCase(),
                subJobType: subJobType?.toLowerCase() || "n/a",
                tradeCustomer,
                taxReq,
                termsAndConsCheck,
                newsletterCheck,
                uploadedDocs: imageFiles,
              })
            })
            .catch(err => {
              setLoading(false)
              props.showToaster(i18n.serverError)
              //   log.error(err);
              addDataLayer({
                title: "submit",
                sts: "failure",
                msg: i18n?.serverError?.toLowerCase(),
                formTitle,
                jobType: jobType?.toLowerCase(),
                subJobType: subJobType?.toLowerCase() || "n/a",
                tradeCustomer,
                taxReq,
                termsAndConsCheck,
                newsletterCheck,
                uploadedDocs: imageFiles,
              })
            })
        } else {
          setLoading(false)
          props.showToaster(i18n.genError)
        }
      })
    } else {
      addDataLayer({
        title: "submit",
        sts: "failure",
        msg: isInvalid?.toLowerCase(),
        formTitle,
        jobType: jobType?.toLowerCase(),
        subJobType: subJobType?.toLowerCase() || "n/a",
        tradeCustomer,
        taxReq,
        termsAndConsCheck,
        newsletterCheck,
        uploadedDocs: imageFiles,
      })
      setLoading(false)
    }
  }

  return !submitSuccess ? (
    <div className="kf-react-container">
      <div className="trade-membership-main">
        <ContactInfo
          i18n={i18n}
          data={userData}
          setData={setUserData}
          error={error}
          setError={setError}
          onBlur={onBlurError}
        />
        <BusinessInfo
          i18n={i18n}
          data={userData}
          setData={setUserData}
          error={error}
          setError={setError}
          onBlur={onBlurError}
        />
        <UploadDocument
          imageList={imageFiles}
          updateList={setImageFiles}
          i18n={i18n}
          data={userData}
          setData={setUserData}
          formTitle={formTitle}
        />
        <SubmitFooter
          i18n={i18n}
          data={userData}
          setData={setUserData}
          onSubmit={onSubmit}
          error={error}
          legalPages={props.legalPages}
          loading={loading}
          formTitle={formTitle}
          termsAndConsLink={termsAndConsLink}
          subjectAccessRequest={subjectAccessRequest}
          getInternalLinkURLS={getInternalLinkURLS}
          recaptchaAnalytics={recaptchaAnalytics}
        />
      </div>
    </div>
  ) : (
    <TradeMembershipConfirmation
      i18n={i18n}
      setSubmitSuccess={props.handleSubmitMsg}
      continueShoppingUrl={continueShoppingCTALink}
      isNewTab={isNewTab}
      getInternalLinkURLS={getInternalLinkURLS}
    />
  )
}

export default TradeMembership
